/* Dates, the way a gym thinks about them.
 *
 * Ported from the date helpers scattered through acrostic-v1-app/app/js/util.js
 * (`localISO`, `todayISO`, `daysAgoISO`, `daysSince`, `daysUntil`).
 *
 * TWO THINGS THE ORIGINAL LEARNED THE HARD WAY, both kept:
 *
 * 1. A 'YYYY-MM-DD' is parsed at **12:00 local**, never at midnight and never as UTC.
 *    `new Date('2026-09-11')` is midnight UTC, so from 5pm Pacific onwards it is already
 *    tomorrow, and `toISOString().slice(0,10)` on a local Date rolls the day over the same
 *    way. Noon is far enough from both DST boundaries that a 23- or 25-hour day still
 *    floors to the right whole number of days.
 *
 * 2. Everything takes an explicit `today`. util.js reached for `Date.now()` inside
 *    `riskModel` and `daysSince`, which made the model untestable and — see `daysSince`
 *    below — made a member's score change at lunchtime.
 */
/** A Date to 'YYYY-MM-DD' in LOCAL time. `toISOString()` converts to UTC first. */
export function localISO(d) {
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}
/** Today, as the gym's calendar sees it. */
export function todayISO(now = new Date()) {
    return localISO(now);
}
/**
 * Parse a stored date. A 'YYYY-MM-DD' lands at 12:00 local; a full instant is taken as
 * written. Returns null for anything unparseable rather than NaN, so callers can say
 * "not held" instead of quietly scoring zero.
 */
export function parseDay(v) {
    if (!v)
        return null;
    const s = String(v);
    const t = s.length === 10 ? new Date(s + 'T12:00:00').getTime() : new Date(s).getTime();
    return Number.isNaN(t) ? null : t;
}
/** The calendar day a stored date or instant falls on, at 12:00 local. */
function dayOf(v) {
    if (!v)
        return null;
    return parseDay(String(v).slice(0, 10));
}
/**
 * Whole days from `d` to `today`. Null when `d` is missing or unparseable.
 *
 * DELIBERATE CHANGE FROM util.js: the original compared `Date.now()` against a date
 * anchored at noon, so "days since yesterday" was 0 before 12:00 and 1 after it. A
 * member's score therefore moved at lunchtime with nothing having happened, and the
 * live pass disagreed with `riskAt(m, todayISO())` — the rewind pass, which is already
 * day-anchored — for half of every day. Both ends are anchored to a day here.
 */
export function daysSince(d, today = todayISO()) {
    const a = dayOf(d);
    const b = dayOf(today);
    if (a == null || b == null)
        return null;
    return Math.round((b - a) / 86400000);
}
/** Whole days from `today` until `d`. Negative once it has passed. */
export function daysUntil(d, today = todayISO()) {
    const n = daysSince(d, today);
    return n == null ? null : -n;
}
/** The calendar date `n` days before `today`. */
export function daysAgoISO(n, today = todayISO()) {
    const t = parseDay(today);
    if (t == null)
        throw new RangeError(`daysAgoISO: unparseable date ${today}`);
    return localISO(new Date(t - n * 86400000));
}
export function clamp(n, lo, hi) {
    return Math.max(lo, Math.min(hi, n));
}
